import React, { useState } from "react";
import { Card, Col, Empty, Row, Segmented, Spin, Statistic, Table, Tag, Tooltip } from "antd";
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { useCampaignReports } from "../../../../data/hooks/useCampaignReports";

const SHOP_OPTIONS = [
  { label: "Alle shops", value: "all" },
  { label: "VCE", value: "VCE" },
  { label: "HVO", value: "HVO" },
];

const StatCard = ({ title, value, suffix, prefix, tooltip, color }) => (
  <Card style={{ height: "100%" }} bodyStyle={{ padding: "20px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
      <span style={{ fontSize: "13px", color: "#8c8c8c" }}>{title}</span>
      {tooltip && (
        <Tooltip title={tooltip}>
          <InfoCircleOutlined style={{ fontSize: "12px", color: "#bfbfbf" }} />
        </Tooltip>
      )}
    </div>
    <Statistic
      value={value}
      prefix={prefix}
      suffix={suffix}
      valueStyle={{ fontSize: "24px", fontWeight: 600, color: color || "#262626" }}
    />
  </Card>
);

const breakdownColumns = (groupLabel) => [
  { title: groupLabel, dataIndex: "key", key: "key", render: (v) => <strong>{v}</strong> },
  { title: "Verzonden", dataIndex: "sent", key: "sent", sorter: (a, b) => a.sent - b.sent },
  { title: "Opens", dataIndex: "opened", key: "opened", sorter: (a, b) => a.opened - b.opened },
  {
    title: "Open rate",
    dataIndex: "openRate",
    key: "openRate",
    render: (v) => <Tag>{(v || 0).toFixed(1)}%</Tag>,
    sorter: (a, b) => a.openRate - b.openRate,
  },
  { title: "Clicks", dataIndex: "clicked", key: "clicked", sorter: (a, b) => a.clicked - b.clicked },
  {
    title: "Click rate",
    dataIndex: "clickRate",
    key: "clickRate",
    render: (v) => <Tag>{(v || 0).toFixed(1)}%</Tag>,
    sorter: (a, b) => a.clickRate - b.clickRate,
  },
  { title: "Orders", dataIndex: "orders", key: "orders", sorter: (a, b) => a.orders - b.orders },
  {
    title: "Omzet",
    dataIndex: "revenue",
    key: "revenue",
    render: (v) => CURRENCY + formatPrice(v || 0),
    sorter: (a, b) => a.revenue - b.revenue,
  },
  {
    title: "Conversie",
    dataIndex: "conversionRate",
    key: "conversionRate",
    render: (v) => (
      <Tag color={v >= 10 ? "success" : v >= 3 ? "warning" : "default"}>{(v || 0).toFixed(1)}%</Tag>
    ),
    sorter: (a, b) => a.conversionRate - b.conversionRate,
  },
];

const CampaignReportsDashboard = () => {
  const [shopSource, setShopSource] = useState("all");
  const { stats, isLoading, error, refresh } = useCampaignReports({ shopSource });

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <Card>
          <Empty description="Er is een fout opgetreden bij het laden van de campagne rapportage." />
        </Card>
      </DashboardContainer>
    );
  }

  const pd = stats.personalDiscounts;

  return (
    <DashboardContainer>
      <Row gutter={16} className="mb-3">
        <Col xs={24} sm={18}>
          <PageTitle>Campagne Rapportage</PageTitle>
          <SubTitle>Verstuurde mails, conversie en persoonlijke kortingen per segment en campagne</SubTitle>
        </Col>
        <Col xs={24} sm={6} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <ReloadOutlined onClick={() => refresh()} style={{ fontSize: 18, cursor: "pointer", color: "#1890ff" }} />
        </Col>
      </Row>

      <Row gutter={16} className="mb-3">
        <Col xs={24}>
          <Segmented options={SHOP_OPTIONS} value={shopSource} onChange={setShopSource} size="large" />
        </Col>
      </Row>

      {stats.totalSent === 0 ? (
        <Card>
          <Empty description="Nog geen verstuurde campagne-mails. Zodra campagnes draaien verschijnen hier de resultaten." />
        </Card>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard title="Verzonden mails" value={stats.totalSent} color="#1890ff" />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Resulterende orders"
                value={stats.totalOrders}
                tooltip="Orders toegeschreven aan een campagne-mail (via order-attributie)."
                color="#52c41a"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Toegeschreven omzet"
                value={formatPrice(stats.totalRevenue || 0)}
                prefix={CURRENCY}
                color="#52c41a"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Conversie"
                value={(stats.conversionRate || 0).toFixed(1)}
                suffix="%"
                tooltip="Aandeel verzonden mails dat tot een toegeschreven order leidde."
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Opens"
                value={stats.totalOpened}
                tooltip="Unieke opens volgens SMTP2GO (ongeveer)."
                color="#1890ff"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Clicks"
                value={stats.totalClicked}
                tooltip="Unieke clicks volgens SMTP2GO."
                color="#1890ff"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Open rate"
                value={(stats.openRate || 0).toFixed(1)}
                suffix="%"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Click rate"
                value={(stats.clickRate || 0).toFixed(1)}
                suffix="%"
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard title="Mislukt" value={stats.totalFailed} color="#cf1322" />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <StatCard
                title="Overgeslagen (uitgeschreven)"
                value={stats.totalSkipped}
                tooltip="Niet verzonden omdat de ontvanger is uitgeschreven."
              />
            </Col>
          </Row>

          <Card title="Persoonlijke kortingen" style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic title="Uitgegeven" value={pd.issued} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Ingewisseld" value={pd.redeemed} valueStyle={{ color: "#52c41a" }} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Nog actief" value={pd.active} valueStyle={{ color: "#1890ff" }} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Inwisselratio" value={(pd.redemptionRate || 0).toFixed(1)} suffix="%" />
              </Col>
            </Row>
          </Card>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card title="Per segment">
                <Table
                  size="small"
                  rowKey="key"
                  columns={breakdownColumns("Segment")}
                  dataSource={stats.bySegment}
                  pagination={false}
                  scroll={{ x: 500 }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Per mailtype">
                <Table
                  size="small"
                  rowKey="key"
                  columns={breakdownColumns("Mailtype")}
                  dataSource={stats.byMailType}
                  pagination={false}
                  scroll={{ x: 500 }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Per campagne" style={{ marginTop: 16 }}>
            <Table
              size="small"
              rowKey="key"
              columns={breakdownColumns("Campagne")}
              dataSource={stats.byCampaign}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 500 }}
            />
          </Card>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card style={{ backgroundColor: "#f6f8fa" }}>
                <div style={{ fontSize: "12px", color: "#595959" }}>
                  <strong>Let op:</strong> orders en omzet worden gevuld via order-attributie
                  (`resultingOrderId` / `resultingRevenue` op `campaignSends`). Open- en click-rates
                  worden elke uur gesynchroniseerd vanuit SMTP2GO op basis van `smtp2goMessageId`.
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </DashboardContainer>
  );
};

const DashboardContainer = styled.div`
  padding: 1rem;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin-bottom: 0;
`;

const SubTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 0;
  margin-top: 4px;
`;

export default CampaignReportsDashboard;
